import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/lib/auth";
import { StatusChip, STATUS_STEPS } from "@/components/site/CaseStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { AREAS } from "@/lib/content";
import { Plus, LogOut, Users, Briefcase, UserCog, Download, ShoppingBag, Stamp, Package, Mail, Phone, CheckCircle2, Clock } from "lucide-react";

const inputClass =
    "bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] placeholder:text-[#A1A1AA]/60";

export default function Admin() {
    const { user, logout } = useAuth();
    const [cases, setCases] = useState([]);
    const [users, setUsers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [orderFilter, setOrderFilter] = useState("all"); // all | notarius | paket
    const [newCase, setNewCase] = useState({
        title: "",
        area: AREAS[0].title,
        description: "",
        client_email: "",
        status: "Inkommit",
    });
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = async () => {
        try {
            const [c, u, o] = await Promise.all([
                axios.get(`${API}/cases`, { withCredentials: true }),
                axios.get(`${API}/admin/users`, { withCredentials: true }),
                axios.get(`${API}/admin/shop-orders`, { withCredentials: true }),
            ]);
            setCases(c.data.items || []);
            setUsers(u.data.items || []);
            setOrders(o.data.items || []);
        } catch {
            toast.error("Kunde inte hämta data.");
        }
    };

    useEffect(() => {
        load();
    }, []);

    const createCase = async () => {
        if (!newCase.title.trim() || !newCase.client_email.trim()) {
            toast.error("Titel och klient-e-post krävs.");
            return;
        }
        try {
            await axios.post(`${API}/cases`, newCase, {
                withCredentials: true,
            });
            toast.success("Ärendet skapades.");
            setDialogOpen(false);
            setNewCase({
                title: "",
                area: AREAS[0].title,
                description: "",
                client_email: "",
                status: "Inkommit",
            });
            load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kunde inte skapa ärendet.",
            );
        }
    };

    const changeRole = async (uid, role) => {
        try {
            await axios.patch(
                `${API}/admin/users/${uid}/role`,
                { role },
                { withCredentials: true },
            );
            toast.success("Rollen uppdaterades.");
            load();
        } catch {
            toast.error("Kunde inte byta roll.");
        }
    };

    const updateOrderStatus = async (orderId, status, note) => {
        try {
            const body = { status };
            if (note !== undefined) body.admin_note = note;
            await axios.patch(`${API}/admin/shop-orders/${orderId}`, body, {
                withCredentials: true,
            });
            toast.success("Beställningen uppdaterades.");
            load();
        } catch {
            toast.error("Kunde inte uppdatera beställningen.");
        }
    };

    const filteredOrders =
        orderFilter === "all"
            ? orders
            : orders.filter((o) => (o.category || "paket") === orderFilter);

    return (
        <section data-testid="page-admin" className="py-14 min-h-[70vh]">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <p className="overline">Administration</p>
                        <h1 className="mt-3 font-serif font-light text-4xl sm:text-5xl tracking-tight">
                            Välkommen,{" "}
                            <span className="italic text-[#C4A977]">
                                {user?.name?.split(" ")[0] || "jurist"}
                            </span>
                        </h1>
                    </div>
                    <div className="flex gap-3">
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    data-testid="admin-new-case-btn"
                                    className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                                >
                                    <Plus size={14} className="mr-2" /> Nytt ärende
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#111113] border-white/10 text-[#F4F4F5] max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-serif text-2xl">
                                        Skapa nytt ärende
                                    </DialogTitle>
                                    <DialogDescription className="text-[#A1A1AA]">
                                        Klienten kopplas automatiskt när denne loggar in med samma
                                        Google-e-post.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 mt-2">
                                    <div>
                                        <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">
                                            Titel
                                        </Label>
                                        <Input
                                            data-testid="new-case-title"
                                            value={newCase.title}
                                            onChange={(e) =>
                                                setNewCase((v) => ({
                                                    ...v,
                                                    title: e.target.value,
                                                }))
                                            }
                                            placeholder="Ex. Företagsöverlåtelse Andersson AB"
                                            className={`mt-2 ${inputClass}`}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">
                                            Klientens e-post
                                        </Label>
                                        <Input
                                            data-testid="new-case-email"
                                            value={newCase.client_email}
                                            onChange={(e) =>
                                                setNewCase((v) => ({
                                                    ...v,
                                                    client_email: e.target.value,
                                                }))
                                            }
                                            placeholder="klient@exempel.se"
                                            type="email"
                                            className={`mt-2 ${inputClass}`}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">
                                                Rättsområde
                                            </Label>
                                            <Select
                                                value={newCase.area}
                                                onValueChange={(v) =>
                                                    setNewCase((s) => ({ ...s, area: v }))
                                                }
                                            >
                                                <SelectTrigger className={`mt-2 ${inputClass}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#111113] border-white/10 text-[#F4F4F5]">
                                                    {AREAS.map((a) => (
                                                        <SelectItem key={a.id} value={a.title}>
                                                            {a.title}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">
                                                Status
                                            </Label>
                                            <Select
                                                value={newCase.status}
                                                onValueChange={(v) =>
                                                    setNewCase((s) => ({ ...s, status: v }))
                                                }
                                            >
                                                <SelectTrigger className={`mt-2 ${inputClass}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#111113] border-white/10 text-[#F4F4F5]">
                                                    {STATUS_STEPS.map((s) => (
                                                        <SelectItem key={s} value={s}>
                                                            {s}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">
                                            Beskrivning
                                        </Label>
                                        <Textarea
                                            data-testid="new-case-description"
                                            value={newCase.description}
                                            onChange={(e) =>
                                                setNewCase((v) => ({
                                                    ...v,
                                                    description: e.target.value,
                                                }))
                                            }
                                            rows={3}
                                            className={`mt-2 ${inputClass}`}
                                        />
                                    </div>
                                </div>
                                <DialogFooter className="mt-4">
                                    <Button
                                        data-testid="new-case-submit"
                                        onClick={createCase}
                                        className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                                    >
                                        Skapa ärende
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Link to="/resurser" data-testid="admin-resurser-link">
                            <Button
                                variant="outline"
                                className="border-white/10 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm w-full sm:w-auto"
                            >
                                <Download size={14} className="mr-2" /> Resurser
                            </Button>
                        </Link>
                        <Button
                            data-testid="admin-logout"
                            onClick={logout}
                            variant="outline"
                            className="border-white/10 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm"
                        >
                            <LogOut size={14} className="mr-2" /> Logga ut
                        </Button>
                    </div>
                </div>

                <Tabs defaultValue="cases" className="mt-12">
                    <TabsList className="bg-[#111113] border border-white/10 rounded-sm h-auto p-1">
                        <TabsTrigger
                            value="cases"
                            data-testid="admin-tab-cases"
                            className="data-[state=active]:bg-[#C4A977] data-[state=active]:text-black rounded-sm px-4 py-2"
                        >
                            <Briefcase size={14} className="mr-2" /> Ärenden ({cases.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="users"
                            data-testid="admin-tab-users"
                            className="data-[state=active]:bg-[#C4A977] data-[state=active]:text-black rounded-sm px-4 py-2"
                        >
                            <Users size={14} className="mr-2" /> Användare ({users.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="orders"
                            data-testid="admin-tab-orders"
                            className="data-[state=active]:bg-[#C4A977] data-[state=active]:text-black rounded-sm px-4 py-2"
                        >
                            <ShoppingBag size={14} className="mr-2" /> Beställningar ({orders.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="cases" className="mt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {cases.length === 0 && (
                                <p className="text-[#A1A1AA]">Inga ärenden ännu.</p>
                            )}
                            {cases.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/portal/arende/${c.id}`}
                                    data-testid={`admin-case-${c.id}`}
                                    className="group bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-6 rounded-sm transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="overline !text-[0.65rem]">{c.area}</p>
                                            <h3 className="mt-2 font-serif text-2xl group-hover:text-[#C4A977]">
                                                {c.title}
                                            </h3>
                                            <p className="mt-2 text-xs text-[#A1A1AA]">
                                                {c.client_email}{" "}
                                                {!c.client_user_id && (
                                                    <span className="text-amber-300">
                                                        · ej kopplad
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        <StatusChip status={c.status} />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="users" className="mt-6">
                        <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden">
                            <div className="grid grid-cols-[2fr_2fr_1fr_auto] gap-4 px-6 py-4 border-b border-white/5 text-xs text-[#A1A1AA] uppercase tracking-widest">
                                <span>Namn</span>
                                <span>E-post</span>
                                <span>Roll</span>
                                <span>Åtgärd</span>
                            </div>
                            {users.map((u) => (
                                <div
                                    key={u.user_id}
                                    data-testid={`admin-user-${u.user_id}`}
                                    className="grid grid-cols-[2fr_2fr_1fr_auto] gap-4 px-6 py-4 border-b border-white/5 items-center text-sm"
                                >
                                    <span className="text-[#F4F4F5]">{u.name}</span>
                                    <span className="text-[#A1A1AA]">{u.email}</span>
                                    <span
                                        className={
                                            u.role === "admin"
                                                ? "text-[#C4A977]"
                                                : "text-[#A1A1AA]"
                                        }
                                    >
                                        {u.role}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid={`admin-toggle-role-${u.user_id}`}
                                        onClick={() =>
                                            changeRole(
                                                u.user_id,
                                                u.role === "admin" ? "client" : "admin",
                                            )
                                        }
                                        className="border-white/10 rounded-sm text-xs"
                                    >
                                        <UserCog size={12} className="mr-1" />
                                        {u.role === "admin" ? "Gör till klient" : "Gör till admin"}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="orders" className="mt-6">
                        <div className="flex items-center gap-2 mb-5 flex-wrap">
                            {[
                                { key: "all", label: "Alla" },
                                { key: "notarius", label: "Notarius" },
                                { key: "paket", label: "Paket" },
                            ].map((f) => (
                                <button
                                    key={f.key}
                                    data-testid={`admin-orders-filter-${f.key}`}
                                    onClick={() => setOrderFilter(f.key)}
                                    className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
                                        orderFilter === f.key
                                            ? "bg-[#C4A977] text-black border-[#C4A977]"
                                            : "text-[#A1A1AA] border-white/10 hover:text-[#F4F4F5] hover:border-white/20"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                            <span className="ml-auto text-xs text-[#A1A1AA]">
                                {filteredOrders.length} visas
                            </span>
                        </div>

                        {filteredOrders.length === 0 ? (
                            <p className="text-[#A1A1AA]">Inga beställningar att visa.</p>
                        ) : (
                            <div className="space-y-4">
                                {filteredOrders.map((o) => (
                                    <OrderCard
                                        key={o.id}
                                        order={o}
                                        onUpdate={updateOrderStatus}
                                    />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </section>
    );
}


const ORDER_STATUS_META = {
    pending: { label: "Ej betald", color: "text-amber-300 border-amber-300/30 bg-amber-300/5" },
    paid: { label: "Betald", color: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5" },
    booked: { label: "Bokad", color: "text-[#C4A977] border-[#C4A977]/40 bg-[#C4A977]/10" },
    completed: { label: "Klar", color: "text-[#C4A977] border-[#C4A977]/60 bg-[#C4A977]/15" },
    cancelled: { label: "Avbokad", color: "text-red-300 border-red-300/30 bg-red-300/5" },
};

function OrderCard({ order, onUpdate }) {
    const [noteOpen, setNoteOpen] = useState(false);
    const [note, setNote] = useState(order.admin_note || "");
    const isNotarius = (order.category || "paket") === "notarius";
    const meta = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.pending;
    const created = order.created_at ? new Date(order.created_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "-";

    return (
        <div
            data-testid={`admin-order-${order.id}`}
            className="bg-[#111113] border border-white/5 rounded-sm p-6"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-1">
                        {isNotarius ? (
                            <Stamp size={12} className="text-[#C4A977]" />
                        ) : (
                            <Package size={12} className="text-[#C4A977]" />
                        )}
                        <p className="overline !text-[0.6rem]">
                            {isNotarius ? "Notarius" : "Paket"}  ·  {order.id}  ·  {created}
                        </p>
                    </div>
                    <h3 className="font-serif text-xl text-[#F4F4F5]">{order.product_name}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#A1A1AA]">
                        <span className="text-[#F4F4F5]/90 font-medium">{order.customer_name || "—"}</span>
                        {order.customer_email && (
                            <a
                                href={`mailto:${order.customer_email}`}
                                className="inline-flex items-center gap-1.5 hover:text-[#C4A977]"
                            >
                                <Mail size={11} /> {order.customer_email}
                            </a>
                        )}
                        {order.customer_phone && (
                            <a
                                href={`tel:${order.customer_phone.replace(/\s+/g, "")}`}
                                className="inline-flex items-center gap-1.5 hover:text-[#C4A977]"
                            >
                                <Phone size={11} /> {order.customer_phone}
                            </a>
                        )}
                    </div>
                    {order.notes && (
                        <p className="mt-3 text-xs text-[#A1A1AA] italic border-l-2 border-[#C4A977]/40 pl-3 leading-relaxed">
                            "{order.notes}"
                        </p>
                    )}
                    {order.admin_note && (
                        <p className="mt-3 text-xs text-[#C4A977] inline-flex items-start gap-2">
                            <CheckCircle2 size={11} className="mt-0.5 shrink-0" />
                            <span>{order.admin_note}</span>
                        </p>
                    )}
                </div>

                <div className="text-right">
                    <div className="font-serif text-2xl text-[#C4A977] leading-none">
                        {(order.amount_sek || 0).toLocaleString("sv-SE")} <span className="text-xs text-[#A1A1AA]">kr</span>
                    </div>
                    {order.discount_sek > 0 && (
                        <p className="mt-1 text-[10px] text-[#A1A1AA]">
                            ord. {order.list_price_sek} kr — 50 kr online-rabatt
                        </p>
                    )}
                    <span
                        data-testid={`admin-order-status-${order.id}`}
                        className={`mt-3 inline-block px-2.5 py-1 text-[10px] uppercase tracking-widest border rounded-sm ${meta.color}`}
                    >
                        {meta.label}
                    </span>
                </div>
            </div>

            {/* Actions */}
            <div className="mt-5 pt-5 border-t border-white/5 flex flex-wrap items-center gap-2">
                {order.status === "paid" && (
                    <Button
                        size="sm"
                        data-testid={`admin-order-mark-booked-${order.id}`}
                        onClick={() => setNoteOpen(true)}
                        className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm text-xs h-8"
                    >
                        <Clock size={12} className="mr-1.5" /> Markera som bokad
                    </Button>
                )}
                {(order.status === "paid" || order.status === "booked") && (
                    <Button
                        size="sm"
                        variant="outline"
                        data-testid={`admin-order-mark-completed-${order.id}`}
                        onClick={() => onUpdate(order.id, "completed")}
                        className="border-white/10 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm text-xs h-8"
                    >
                        <CheckCircle2 size={12} className="mr-1.5" /> Markera som klar
                    </Button>
                )}
                {order.status !== "cancelled" && order.status !== "completed" && (
                    <Button
                        size="sm"
                        variant="outline"
                        data-testid={`admin-order-cancel-${order.id}`}
                        onClick={() => {
                            if (window.confirm("Avboka beställningen? Kunden behöver då kontaktas manuellt om eventuell återbetalning.")) {
                                onUpdate(order.id, "cancelled");
                            }
                        }}
                        className="border-white/10 hover:border-red-300/60 hover:text-red-300 rounded-sm text-xs h-8"
                    >
                        Avboka
                    </Button>
                )}
            </div>

            {/* Booking note dialog */}
            <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
                <DialogContent className="bg-[#111113] border-white/10 text-[#F4F4F5] max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl">Markera som bokad</DialogTitle>
                        <DialogDescription className="text-xs text-[#A1A1AA]">
                            Lägg till tid och plats för besöket — visas både här och kan återfinnas i ordern.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2">
                        <Label className="text-xs text-[#A1A1AA] uppercase tracking-widest">
                            Bokningsnotering
                        </Label>
                        <Textarea
                            data-testid={`admin-order-note-input-${order.id}`}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="T.ex. Tis 5 maj kl 14:00 – Stockholmskontoret. Medta pass."
                            rows={4}
                            className={`mt-2 ${inputClass} !h-auto py-3`}
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button
                            data-testid={`admin-order-note-save-${order.id}`}
                            onClick={() => {
                                onUpdate(order.id, "booked", note);
                                setNoteOpen(false);
                            }}
                            className="bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm"
                        >
                            Spara bokning
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
